import React, { useRef, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import Form from '@rjsf/core'
import styled from 'styled-components'
import { WidgetProps } from '@rjsf/utils'
import { AnyObject, parseJsonSafe, stringifyJsonSafe } from '@feedyou/utils'

import { useNlpModels } from '../../../../store/flow'
import { Button } from '../../../design-system/Button'
import { runNlpTestThunk } from '../../../../features/nlpTester'
import { useNlpModelAndIntentFromModalDescriptor } from './hooks'
import { useRjsfValidator } from '../../../design-system/FeedbotRjsfForm/hooks'
import { isModelChoosen } from '../../sharedSchemaFragments/nlpModelsTest/utils'
import { injectModelsToSchemaWithoutExpectedModelMatchOption as injectModels } from '../../sharedSchemaFragments/nlpModelsTest/utils'
import {
  schema as sharedSchemaFragment,
  uiSchema as uiSchemaSharedSchemaFragment
} from '../../sharedSchemaFragments/nlpModelsTest/constants'

export const TestSectionWidget: React.FC<WidgetProps> = props => {
  const models = useNlpModels()
  const dispatch = useDispatch()
  const [isTesting, setIsTesting] = useState<boolean | undefined>()
  const { model, intent } = useNlpModelAndIntentFromModalDescriptor()
  const value = useMemo(() => parseJsonSafe<AnyObject>(props.value, {}), [props.value])

  return (
    <Content>
      <FormContent>
        <FormDescription>Test your final Intent Description:</FormDescription>
        <TestAgainstModelsForm {...{ ...props, value }} />
      </FormContent>
      <TestButton
        label="Test"
        loading={isTesting}
        disabled={isTesting}
        minMaxSizes={{ minWidth: 100, maxWidth: 100, maxHeight: 36 }}
        onClick={async () => {
          setIsTesting(true)
          try {
            await dispatch(
              runNlpTestThunk({
                onlyQueryAgainstExpectedModel: false,
                input: [props.formContext.formData.utterance, model?.id || '', intent.id].join(
                  '\t'
                ),
                models:
                  value?.testAgainstModels?.mode === 'CHOOSE'
                    ? models.filter(isModelChoosen(value))
                    : models
              })
            )
          } catch (error) {
            console.error((error as Error)?.message)
          }
          setIsTesting(false)
        }}
      />
    </Content>
  )
}

export const TestAgainstModelsForm: React.FC<WidgetProps> = props => {
  const models = useNlpModels()
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <StyledForm
      uiSchema={uiSchema}
      formData={props.value}
      validator={useRjsfValidator()}
      schema={injectModels<typeof schema>(schema, models) as any}
      onChange={({ formData }) => props.onChange(stringifyJsonSafe({ json: formData }))}
    >
      <button hidden type="submit" ref={submitButtonRef} />
    </StyledForm>
  )
}

const Content = styled.span`
  display: flex;
  flex-direction: row;
`

const FormDescription = styled.strong`
  margin-bottom: 5px;
`

const FormContent = styled.div`
  flex: 1;
  display: flex;
  margin-top: 5px;
  flex-direction: column;
`

const StyledForm = styled(Form)<{ onChange(formData: AnyObject): void }>`
  margin-left: -10px;
`

const TestButton = styled(Button)`
  padding: 0;
  min-width: 100px;
  margin: 30px 0px auto 15px;
`

export const uiSchema = {
  testAgainstModels: uiSchemaSharedSchemaFragment.testAgainstModels
}

export const schema = {
  type: 'object',
  properties: { testAgainstModels: sharedSchemaFragment.properties.testAgainstModels }
} as const
